import React, { FC, useEffect, useState } from 'react';
import { MxButton } from '@guildeducationinc/playground-mx';
import { Alert, ShimmerLoader, Tag, Typography } from '@guildeducationinc/recess';
import { trackEvent } from '@src/services/analytics';
import {
	GetMemberProgramQuery,
	useCreateNotifyOptInMutation,
	useGetNotifyMeWhenOptInQuery,
} from '@src/graphqlTypes/catalogServiceTypes';
import NotificationsIcon from '@guildeducationinc/recess/icons/NotificationsIcon';
import { segmentCase } from '@guildeducationinc/catalog-tools';
import { logDataDogError } from '@src/utilities/datadog';
import { AlertContent } from '@guildeducationinc/recess/components/Alert/AlertContent';
import { getNotifyOptIn } from '@src/queries/notifyMeWhen';
import styles from './CappedDateCta.module.scss';

interface CappedDateCtaProps {
	programId: GetMemberProgramQuery['memberProgram']['id'];
}

export const NOTIFY_ME_WHEN_BUTTON_TEXT = 'Notify me when I can apply';

export const CappedDateCtaNotifyMe: FC<CappedDateCtaProps> = ({ programId }) => {
	const [error, setError] = useState(false);

	useEffect(() => {
		if (programId) {
			trackEvent('notifyMeWhenViewed', {
				product: 'catalog',
				programId,
				tag: 'program_highlight_panel',
			});
		}
	}, [programId]);

	const [createNotifyOptIn, { data: createData, loading: createLoading }] =
		useCreateNotifyOptInMutation({
			variables: {
				cappedProgramData: {
					programId,
				},
			},
			onCompleted: () => {
				trackEvent('notifyMeWhenClicked', {
					product: 'catalog',
					programId,
					tag: 'program_highlight_panel',
					text: `${segmentCase(NOTIFY_ME_WHEN_BUTTON_TEXT)}`,
					type: 'button',
				});
			},
			refetchQueries: [
				{
					query: getNotifyOptIn,
					variables: {
						programId,
					},
				},
			],
			onError: error => {
				setError(true);
				logDataDogError(error, 'createNotifyOptInError');
			},
		});

	const { data: getData, loading: getLoading } = useGetNotifyMeWhenOptInQuery({
		variables: {
			programId,
		},
		skip: !programId,
		onError: error => {
			logDataDogError(error, 'getNotifyOptInError');
		},
	});

	const loading = createLoading || getLoading;
	const notifyMeClicked =
		createData?.createNotifyOptIn.programId || getData?.getNotifyMeWhenOptIn.programId;

	return (
		<>
			<Typography gutterBottom='sm' testid='capped-date-cta' variant='label1'>
				This program is not currently accepting applications.
			</Typography>

			<ShimmerLoader label='Notification confirmation' loaded={!loading}>
				{notifyMeClicked ? (
					<div className={styles.notifyContainer} data-testid='notify-me-confirmation'>
						<NotificationsIcon className={styles.notifyIcon} size={24} />
						<Typography variant='body1'>
							You will be notified when you can apply to this program.
						</Typography>
					</div>
				) : (
					<MxButton
						appearance='outline'
						className={styles.notifyButton}
						fullWidth={true}
						onClick={() => createNotifyOptIn()}
						testid='notify-me-button'
					>
						{NOTIFY_ME_WHEN_BUTTON_TEXT}
					</MxButton>
				)}

				<Alert className={styles.notifyError} display={error} hideClose type='danger'>
					<AlertContent>Unable to create notification opt in, please try again later.</AlertContent>
				</Alert>
			</ShimmerLoader>

			<div className={styles.cappedTag}>
				<Tag appearance='quiet' color='yellow'>
					Program is full
				</Tag>
			</div>
		</>
	);
};
